/**
 * OpenCodeTuiAdapter — drives the `opencode` TUI (https://opencode.ai) as a
 * raw relayed terminal. v1 declares NO structured events (no trusted
 * transcript/hook source is wired), no prefill, no passive turns, and no
 * resume (--continue behavior on an empty state dir is unverified; enable
 * after live verification). Auth is env-based for api_key keys
 * (CONVEYOR_AGENT_KEY → provider-native env var) and store-based for
 * chatgpt_oauth keys (CONVEYOR_OPENCODE_OAUTH seeded into opencode's
 * auth.json by prepareEnvironment — see opencode-auth.ts).
 */
import { buildPromptBytes, inheritedEnv } from "../pty-support.js";
import { cleanTerminalOutput } from "../spawn-args.js";
import { seedOpenCodeOauth } from "./opencode-auth.js";
import {
  findOnPath,
  TuiUnavailableError,
  type TuiAdapter,
  type TuiCapabilities,
  type TuiFingerprintInput,
  type TuiSpawnInput,
  type TuiSpawnSpec,
} from "./types.js";

/** provider → the env var its SDK/CLI reads natively. */
const PROVIDER_KEY_ENV: Record<string, string> = {
  openai: "OPENAI_API_KEY",
  anthropic: "ANTHROPIC_API_KEY",
};

export class OpenCodeTuiAdapter implements TuiAdapter {
  readonly id = "opencode" as const;
  readonly capabilities: TuiCapabilities = {
    resume: false,
    structuredEvents: false,
    prefill: false,
    passiveTurns: false,
  };

  constructor(private readonly env: NodeJS.ProcessEnv = process.env) {}

  resolveBinary(env: NodeJS.ProcessEnv = this.env): string {
    const override = env.CONVEYOR_OPENCODE_BIN;
    const found = override ? findOnPath(override, env) : findOnPath("opencode", env);
    if (!found) {
      throw new TuiUnavailableError(
        "opencode",
        "The opencode CLI is not available in this environment. It must be baked " +
          "into the pod image (see Dockerfile.base) — re-run Build Image for this " +
          "project, or set CONVEYOR_OPENCODE_BIN to its absolute path.",
      );
    }
    return found;
  }

  buildSpawn(input: TuiSpawnInput): TuiSpawnSpec {
    const env = { ...inheritedEnv() };
    delete env.CONVEYOR_AGENT_KEY;
    delete env.CONVEYOR_OPENCODE_OAUTH;
    const oauthActive = Boolean(this.env.CONVEYOR_OPENCODE_OAUTH);
    const key = this.env.CONVEYOR_AGENT_KEY;
    const provider = this.env.CONVEYOR_AGENT_PROVIDER ?? "openai";
    const keyEnvVar = PROVIDER_KEY_ENV[provider];
    // ChatGPT-subscription auth flows through the opencode auth store (see
    // opencode-auth.ts) — a provider API key would override it to paid billing.
    if (key && keyEnvVar && !oauthActive) env[keyEnvVar] = key;

    const model = this.env.CONVEYOR_AGENT_MODEL ?? input.options.model;
    const args: string[] = [];
    if (model) args.push("--model", model.includes("/") ? model : `${provider}/${model}`);
    return { file: this.resolveBinary(), args, env };
  }

  async prepareEnvironment(): Promise<void> {
    // ChatGPT-subscription tokens (when present) are seeded into opencode's
    // auth store; session-state persistence stays the entrypoint's job
    // (GCS-FUSE symlinks for ~/.local/share/opencode).
    await seedOpenCodeOauth(this.env);
  }

  spawnFingerprint(input: TuiFingerprintInput): string {
    return JSON.stringify(["opencode", input.model, input.cwd]);
  }

  encodePromptBytes(text: string): string {
    // Bracketed paste — standard for modern TUIs; revisit if live verification
    // shows opencode's input mishandles it.
    return buildPromptBytes(text);
  }

  buildExitErrors(exitCode: number, rawOutput: string): string[] {
    const errors = [`opencode exited (code ${exitCode}) without a result`];
    const tail = cleanTerminalOutput(rawOutput);
    if (tail) errors.push(`Last terminal output before exit:\n${tail}`);
    return errors;
  }
}
